import { useTranslation } from 'react-i18next';
import type { WorkspaceExport } from '@/api/workspaceExport/workspaceExportApi.types';
import { Icon } from '@/components/common/Icon';
import { Button } from '@/components/ui/button';
import { WORKSPACE_EXPORT_STATUS } from '@/constants/workspaceData';
import { formatIsoDateTimeToTimestamp } from '@/utils/dateFormatting';
import { isWorkspaceExportInProgress } from '@/utils/workspaceExportStatus';

interface WorkspaceExportStatusProps {
  workspaceExport: WorkspaceExport | null;
  isRequesting: boolean;
  isDownloading: boolean;
  onRequest: () => void;
  onDownload: (exportId: string) => void;
}

export function WorkspaceExportStatus({
  workspaceExport,
  isRequesting,
  isDownloading,
  onRequest,
  onDownload,
}: WorkspaceExportStatusProps) {
  const { t } = useTranslation('workspaceData');

  if (workspaceExport === null) {
    return (
      <Button isLoading={isRequesting} onClick={onRequest}>
        {t('export.request')}
      </Button>
    );
  }

  if (isWorkspaceExportInProgress(workspaceExport)) {
    return (
      <p className="inline-flex items-center gap-2 text-[13px] text-zooindigo" role="status">
        <Icon name="Clock" />
        {t('export.preparing')}
      </p>
    );
  }

  if (workspaceExport.status === WORKSPACE_EXPORT_STATUS.ready) {
    const readyMessage =
      workspaceExport.expiresAt === null
        ? t('export.readyWithoutExpiry')
        : t('export.ready', { date: formatIsoDateTimeToTimestamp(workspaceExport.expiresAt) });
    return (
      <div className="flex flex-wrap items-center gap-4">
        <p className="text-[13px] text-zooindigo">{readyMessage}</p>
        <Button
          isLoading={isDownloading}
          onClick={function download() {
            onDownload(workspaceExport.id);
          }}
        >
          <Icon name="Download" />
          {t('export.download')}
        </Button>
      </div>
    );
  }

  const unavailableMessageKey =
    workspaceExport.status === WORKSPACE_EXPORT_STATUS.failed ? 'export.failed' : 'export.expired';
  return (
    <div className="flex flex-wrap items-center gap-4">
      <p className="text-[13px] text-zoomuted">{t(unavailableMessageKey)}</p>
      <Button variant="outlinePrimary" isLoading={isRequesting} onClick={onRequest}>
        {t('export.requestAnother')}
      </Button>
    </div>
  );
}
